import { useEffect, useState } from "react";
import { Play } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

function KeyPoints({ points }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    if (!points?.length) return undefined;
    const t = setInterval(() => setI((x) => (x + 1) % points.length), 4200);
    return () => clearInterval(t);
  }, [points]);
  if (!points?.length) return null;
  return (
    <div className="absolute left-5 right-5 bottom-5 sm:left-8 sm:right-8 sm:bottom-7 pointer-events-none">
      <p className="mono-label text-teal">Key point {i + 1} of {points.length}</p>
      <AnimatePresence mode="wait">
        <motion.p
          key={i}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.45 }}
          className="font-display text-xl sm:text-2xl lg:text-3xl tracking-wide leading-tight mt-2 max-w-2xl drop-shadow"
          data-testid="article-video-keypoint"
        >
          {points[i]}
        </motion.p>
      </AnimatePresence>
    </div>
  );
}

export default function VideoEmbed({ video, poster }) {
  if (!video) return null;

  if (video.youtubeId) {
    return (
      <figure className="card-line rounded-2xl overflow-hidden" data-testid="article-video">
        <div className="aspect-video">
          <iframe className="w-full h-full" src={`https://www.youtube-nocookie.com/embed/${video.youtubeId}?rel=0&modestbranding=1`} title={video.title} loading="lazy" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
        </div>
        <figcaption className="px-6 py-4 text-sm text-mute">{video.title}</figcaption>
      </figure>
    );
  }

  if (video.src) {
    return (
      <figure className="card-line rounded-2xl overflow-hidden" data-testid="article-video">
        <div className="relative aspect-video bg-ink">
          <video
            className="absolute inset-0 w-full h-full object-cover"
            src={video.src}
            poster={poster}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            aria-label={video.title}
            data-testid="article-video-player"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/20 to-transparent" />
          <KeyPoints points={video.points} />
        </div>
        <figcaption className="px-6 py-4 text-xs text-dusk leading-relaxed">
          <span className="text-bone/80">{video.title}.</span> Illustrative film with the article's key
          points; it is not a substitute for the full guide or for clinical advice.
        </figcaption>
      </figure>
    );
  }

  return (
    <figure className="card-line rounded-2xl overflow-hidden relative" data-testid="article-video-placeholder">
      <div className="aspect-video relative">
        {poster && <img src={poster} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" loading="lazy" />}
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-transparent" />
        <div className="absolute inset-0 grid place-items-center">
          <span className="w-16 h-16 rounded-full border border-teal/50 bg-ink/70 backdrop-blur grid place-items-center">
            <Play size={22} className="text-teal translate-x-0.5" />
          </span>
        </div>
        <div className="absolute left-6 bottom-5 right-6">
          <p className="mono-label text-teal">Explainer · in production</p>
          <p className="font-display text-2xl tracking-wide mt-2">{video.title}</p>
        </div>
      </div>
      <figcaption className="px-6 py-4 text-xs text-dusk leading-relaxed">Short films are published here after clinical and regulatory review. No treatment is advertised in any film.</figcaption>
    </figure>
  );
}
